import '../work-order.scss';
import { Modal } from '../../../components/modal/modal';
import { useContext, useEffect, useState } from 'react';
import { SingleValue } from 'react-select';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { UseabilityStatus, WorkOrderType } from '../../../clients/quipli-service/service-and-repair/service-and-repair';
import { Product, ProductInventoryUnit, ProductTrackingType } from '../../../clients/quipli-service/product/product';
import { DateTime } from 'luxon';
import { Spinner } from '../../../components/spinner/spinner';
import { Location } from '../../../clients/quipli-service/location/location';
import { DateTimePicker } from '../../../components/date-picker/date-time-picker';
import { TextArea } from '../../../components/textarea/textarea';
import { TenantContext } from '../../../context/tenant-context';
import { SelectForm } from '../../../components/select-form/select-form';
import { Radio } from '../../../components/radio/radio';
import { Field, FieldLabel } from '../../../components/field/field';
import { useNavigate } from 'react-router-dom';
import { User, UserEquipment } from '../../../clients/quipli-service/user/user';
import { CustomerSelect } from '../../customer/components/customer-select';
import { Typography } from '../../../components/typography/typography';
import { LocationSelect } from '../../location/select/location-select';
import { CustomerEquipmentSelect } from '../../../customer/equipment/equipment-select';

export const WorkOrderForm = ({
  isOpen,
  setIsOpen,
}: {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const api = useAPI();
  const navigate = useNavigate();
  const { contextPath } = useContext(TenantContext);

  // React states
  const [isContentLoading, setContentLoader] = useState<boolean>(false),
    [isRequestInProgress, setRequestInProgress] = useState<boolean>(false),
    [listProducts, setListProducts] = useState<Product[]>([]),
    [listUnits, setListUnits] = useState<ProductInventoryUnit[]>([]),
    [listWorkOrderTypes, setListWorkOrderTypes] = useState<WorkOrderType[]>([]),
    [product, setProduct] = useState<Product | null>(null),
    [unit, setUnit] = useState<ProductInventoryUnit | null>(null),
    [unitAvailability, setUnitAvailability] = useState<string | null>(null),
    [workFrom, setWorkFrom] = useState<DateTime>(),
    [workTo, setWorkTo] = useState<DateTime>(),
    [dateHasError, setDateHasError] = useState<boolean>(false),
    [workType, setWorkType] = useState<WorkOrderType | null>(null),
    [notes, setNotes] = useState<string>(''),
    [locations, setLocations] = useState<Location[] | undefined>(),
    [location, setLocation] = useState<Location | null>(),
    [poNumber, setPoNumber] = useState<string>(),
    [serialNumberEnabled, setSerialNumberEnabled] = useState<boolean>(false),
    [isFleet, setIsFleet] = useState<boolean>(true),
    [customer, setCustomer] = useState<User | null>(null),
    [customerEquipment, setCustomerEquipment] = useState<UserEquipment | null>(null),
    [enableServiceAndRepair, setEnableServiceAndRepair] = useState<boolean>(false);

  useEffect(() => {
    const fetchLocation = async () => {
      if (!product) return;

      let productlocation;
      if (locations) {
        productlocation = locations.find((l) => l.id === product.locationIds[0]);
      } else {
        productlocation = await api.getLocationById(product.locationIds[0]);
      }

      setLocation(productlocation);
    };
    fetchLocation();
  }, [product]);

  useEffect(() => {
    if (unit && locations) {
      const unitLocation = locations.find((l) => l.id === unit.locationId);
      setLocation(unitLocation);
    }
  }, [unit]);

  useEffect(() => {
    const fetchLocations = async () => {
      const locations = await api.listLocations(true);
      setLocations(locations);
    };

    const fetchUnitTrackingConfig = async () => {
      const config = await api.getConfiguration('CONF_UNIT_TRACKING_VIA');
      setSerialNumberEnabled(config?.value === '1');

      const enableServiceAndRepair = await api.getConfiguration('CONF_ENABLE_SERVICE_AND_REPAIR');
      if (enableServiceAndRepair?.value === '1') {
        setEnableServiceAndRepair(true);
      } else {
        setIsFleet(true);
      }
    };

    fetchLocations();
    fetchUnitTrackingConfig();
  }, []);

  // Callbacks
  const submitWorkOrder = async () => {
    if (!workFrom) return;
    let wo;
    if (isFleet) {
      if (!product || !unit || !unitAvailability || !location) return;

      // Block buttons and exit of modal
      setRequestInProgress(true);

      wo = await api.submitFleetWorkOrder({
        from: workFrom,
        to: workTo,
        locationId: location.id,
        productId: product.id,
        unitId: unit.id,
        unitStatus: unitAvailability,
        type: workType?.id,
        notes: notes,
        poNumber: poNumber,
      });
    } else {
      if (!location || !customer || !customerEquipment) return;

      // Block buttons and exit of modal
      setRequestInProgress(true);

      wo = await api.submitCustomerWorkOrder({
        from: workFrom,
        to: workTo,
        locationId: location.id,
        userId: customer.id,
        userEquipmentId: customerEquipment.id,
        type: workType?.id,
        notes: notes,
        poNumber: poNumber,
      });
    }

    // Close modal
    setRequestInProgress(false);
    setIsOpen(false);

    navigate(`${contextPath}manager/workorders/view/${wo.id}`);
  };

  const handleOnChangeProduct = (option: SingleValue<Product>) => {
    setUnit(null);
    // If product is selected, the unit list is also updated
    if (option) {
      setProduct(option);
      setListUnits(option.inventoryUnits.filter((unit) => unit.isActive && unit.deletedAt === undefined));
    } else {
      setProduct(null);
    }
  };

  const isBtnDisabled = (): boolean => {
    return (
      isRequestInProgress ||
      isContentLoading ||
      (isFleet && (product === null || unit === null || unitAvailability === null)) ||
      (!isFleet && (location === null || customer === null || customerEquipment === undefined)) ||
      !workFrom?.isValid ||
      !!(workTo && !workTo.isValid) ||
      dateHasError
    );
  };

  const clear = () => {
    setLocations(undefined);
    setProduct(null);
    setUnit(null);
    setCustomer(null);
    setCustomerEquipment(null);
  };

  const closeModal = () => {
    // Don't close modal if there is a request in progress
    if (isRequestInProgress) return;

    clear();

    setIsOpen(false);
  };

  // Hooks
  useEffect(() => {
    // Load products and work types
    const getContent = async () => {
      setContentLoader(true);

      const [prods, types] = await Promise.all([api.listProducts(), api.listWorkOrderTypes()]);

      setListProducts(prods.filter((prod) => prod.trackingType === ProductTrackingType.SerialUnits));
      setListWorkOrderTypes(types);
      setContentLoader(false);
    };

    getContent();
  }, []);

  useEffect(() => {
    setDateHasError(!!(workFrom && workTo && workTo.diff(workFrom).toMillis() <= 0));
  }, [workFrom, workTo]);

  return (
    <Modal
      isOpen={isOpen}
      title="Create new work order"
      actions={
        isRequestInProgress ? (
          <Spinner />
        ) : (
          <>
            <button
              className="button button-small button-block button-secondary"
              disabled={isRequestInProgress}
              onClick={() => closeModal()}
            >
              Cancel
            </button>

            <button
              className="button button-small button-block button-primary"
              disabled={isBtnDisabled()}
              onClick={() => submitWorkOrder()}
            >
              Create
            </button>
          </>
        )
      }
      close={closeModal}
    >
      {enableServiceAndRepair && (
        <div>
          <div className="flex gap-5 my-2">
            <div className="flex gap-1 items-center">
              <Radio isChecked={isFleet} onChange={() => setIsFleet(true)} />
              <FieldLabel label="Fleet" />
            </div>
            <div className="flex gap-1 items-center">
              <Radio isChecked={!isFleet} onChange={() => setIsFleet(false)} />
              <FieldLabel label="Customer" />
            </div>
          </div>
        </div>
      )}

      {isFleet && (
        <div>
          <div className="text-center px-4 mb-4">
            <Typography type="text-sm" className="text-grey-500">
              During this time period the inventory calendar will reflect an outage
            </Typography>
          </div>
        </div>
      )}

      {/* From */}
      <div>
        <DateTimePicker
          label="Start Work"
          isRequired
          value={workFrom}
          onChange={setWorkFrom}
          timezone={location?.timezone}
        />
      </div>
      {/* To */}
      <div>
        <DateTimePicker label="End Work" value={workTo} onChange={setWorkTo} timezone={location?.timezone} />
        {dateHasError && <span className="text-red-600">Date must be greater than "Start Work"</span>}
      </div>

      {isFleet && (
        <div>
          {/* Product */}
          <SelectForm
            label="Product"
            isRequired
            menuShouldBlockScroll
            menuPosition="fixed"
            menuPortalTarget={document.body}
            options={listProducts}
            value={product}
            onChange={(option) => handleOnChangeProduct(option)}
            getOptionValue={(option) => `${option.id}`}
            getOptionLabel={(option) => option.name}
            isLoading={isContentLoading}
            isDisabled={isContentLoading}
            isClearable={true}
          />

          {/* Unit */}
          <div className="work-order-field mt-4">
            <SelectForm
              label="Unit"
              isRequired
              menuShouldBlockScroll
              menuPosition="fixed"
              menuPortalTarget={document.body}
              options={listUnits}
              value={unit}
              isClearable={true}
              isDisabled={product === null}
              onChange={(option) => setUnit(option)}
              getOptionValue={(option) => `${option.id}`}
              getOptionLabel={(option) => (serialNumberEnabled ? option.serialNumber : option.unitNumber)}
            />
          </div>

          {/* Unit Availability */}
          <div className="work-order-field mt-4">
            <FieldLabel label="Unit Availability" isRequired />

            <div className="flex gap-x-6">
              <div className="flex gap-x-1 items-center">
                <Radio
                  name="unit_status"
                  type="white"
                  label={UseabilityStatus.SoftDown}
                  isChecked={unitAvailability === UseabilityStatus.SoftDown.replace(/\s/g, '')}
                  onChange={(isChecked) =>
                    isChecked && setUnitAvailability(UseabilityStatus.SoftDown.replace(/\s/g, ''))
                  }
                />
              </div>
              <div className="flex gap-x-1 items-center">
                <Radio
                  name="unit_status"
                  type="white"
                  label={UseabilityStatus.HardDown}
                  isChecked={unitAvailability === UseabilityStatus.HardDown.replace(/\s/g, '')}
                  onChange={(isChecked) =>
                    isChecked && setUnitAvailability(UseabilityStatus.HardDown.replace(/\s/g, ''))
                  }
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Location */}
      <div className="work-order-field">
        <FieldLabel label="Location" isRequired />
        <LocationSelect
          locations={locations}
          setLocation={setLocation}
          dataCy="location-select"
          orderLocation={location ?? undefined}
        />
      </div>

      {!isFleet && (
        <div>
          <div className="work-order-field">
            <FieldLabel label="Customer" isRequired />
            <div className="w-full">
              <CustomerSelect setCustomer={setCustomer} disableScanning={true} />
            </div>
          </div>

          <div className="work-order-field mt-4">
            <FieldLabel label="Equipment" isRequired />
            <div className="w-full">
              {customer ? (
                <CustomerEquipmentSelect user={customer} setUserEquipment={setCustomerEquipment} />
              ) : (
                <TextArea className="w-full h-full resize-none text-sm" disabled={true} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* PO Number */}
      <div className="work-order-field">
        <Field
          className="w-full"
          type="text"
          label="PO Number"
          value={poNumber}
          onChange={setPoNumber}
          isRequired={false}
        />
      </div>

      {/* Work Type */}
      <SelectForm
        label="Type"
        menuShouldBlockScroll
        menuPosition="fixed"
        menuPortalTarget={document.body}
        options={listWorkOrderTypes}
        value={workType}
        onChange={(option) => setWorkType(option)}
        getOptionValue={(option) => option.id}
        isLoading={isContentLoading}
        isDisabled={isContentLoading}
        isClearable={true}
      />

      {/* Notes */}
      <div className="w-full">
        <TextArea
          value={notes}
          onChange={({ target }) => setNotes(target.value)}
          rows={5}
          className="form-input text-sm w-full"
          placeholder="Add notes..."
        />
      </div>
    </Modal>
  );
};
